// ============================================================
// FILE: _services/device-notification.service.ts
//
// Backs the header bell icon: one entry per device that has gone from
// connected to disconnected, kept in the panel until the user reads it
// (clicking an entry removes it -- there is no separate "read" flag,
// since an unread notification and a list entry are the same thing
// here). DeviceIntegrationService is the sole caller of notify() -- it
// already tracks every device's connected state and calls in here on a
// true -> false transition.
//
// Also raises a toast for the same event, alongside the bell entry.
// Multilingual support follows the same convention already used by
// Device Integration/Triage's own language dropdowns: the fixed labels
// below are translated on demand via LanguagePreferenceService's backend
// call (multilanguage/transfer) and shown as "English (Translated)"
// through bilingual(), rather than a hardcoded local dictionary. Baked
// into each notification/toast at creation time -- a later language
// change re-words new notifications, not ones already in the list.
// ============================================================
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LanguagePreferenceService } from './language-preference.service';
import { ToastrAppSrvcService } from './toastr-app-srvc.service';

export interface DeviceNotification {
  id: string;
  deviceId: string;
  deviceName: string;
  message: string;
  timestamp: Date;
}

@Injectable({ providedIn: 'root' })
export class DeviceNotificationService {
  private readonly notificationsSubject = new BehaviorSubject<DeviceNotification[]>([]);
  readonly notifications$: Observable<DeviceNotification[]> = this.notificationsSubject.asObservable();
  readonly unreadCount$: Observable<number> = this.notifications$.pipe(map(list => list.length));

  language: string = this.languagePref.currentLanguage;

  private readonly englishLabelText: { [key: string]: string } = {
    disconnectedSuffix: 'disconnected',
    toastTitle: 'Device Disconnected',
    panelTitle: 'Notifications',
    emptyState: 'No new notifications',
    markAsRead: 'Click to mark as read',
  };

  translatedLabels: { [key: string]: string } = { ...this.englishLabelText };

  constructor(
    private readonly languagePref: LanguagePreferenceService,
    private readonly toastr: ToastrAppSrvcService,
  ) {
    // Root-singleton service subscribing in its own constructor and never
    // unsubscribing is intentional here, same as DeviceIntegrationService's
    // own state$ subscriptions -- the subscription lives exactly as long as
    // the service does.
    this.languagePref.language$.subscribe(code => {
      this.language = code;
      if (code !== 'en') this.translateLabels(code);
    });
  }

  /** "English (Translated)" -- same convention as Device Integration's own bilingual(). */
  bilingual(key: string): string {
    const english = this.englishLabelText[key];
    if (english === undefined) return '';
    if (this.language === 'en') return english;
    const translated = this.translatedLabels[key];
    return translated && translated !== english ? `${english} (${translated})` : english;
  }

  private translateLabels(langCode: string): void {
    const labelKeys = Object.keys(this.englishLabelText);
    this.languagePref.translateTexts(labelKeys.map(key => this.englishLabelText[key]), langCode)
      .subscribe({
        next: (data: any) => {
          const translated = typeof data === 'string' ? JSON.parse(data) : data;
          labelKeys.forEach((key, i) => {
            this.translatedLabels[key] = translated?.[i]?.translations?.[0]?.text || this.englishLabelText[key];
          });
        },
        error: () => {},
      });
  }

  /** Adds a disconnect notification for a device, replacing any existing unread one for that same device rather than stacking duplicates, and raises a matching toast. */
  notify(deviceId: string, deviceName: string): void {
    const withoutExisting = this.notificationsSubject.value.filter(n => n.deviceId !== deviceId);
    const message = `${deviceName} ${this.bilingual('disconnectedSuffix')}`;
    const entry: DeviceNotification = {
      id: `${deviceId}-${Date.now()}`,
      deviceId,
      deviceName,
      message,
      timestamp: new Date(),
    };
    this.notificationsSubject.next([entry, ...withoutExisting]);
    this.toastr.ForWarning(message, this.bilingual('toastTitle'));
  }

  /** "Reading" a notification is dismissing it -- it has no further use once the user has seen it. */
  dismiss(id: string): void {
    this.notificationsSubject.next(this.notificationsSubject.value.filter(n => n.id !== id));
  }

  dismissAll(): void {
    this.notificationsSubject.next([]);
  }
}
